/**
 * Permanent (301) redirects from legacy / renamed URLs to their current home.
 * Keys are lowercase paths without a trailing slash or query string.
 * Add new entries here whenever a page is renamed or removed.
 */
export const REDIRECTS: Record<string, string> = {
  // Legacy homepage variants
  "/home": "/",
  "/index": "/",
  "/index.html": "/",
  "/index.php": "/",

  // Renamed top-level pages
  "/about-us": "/about",
  "/our-story": "/about",
  "/contact-us": "/contact",
  "/get-in-touch": "/contact",
  "/our-services": "/services",
  "/what-we-do": "/services",
  "/pricing": "/packages",
  "/plans": "/packages",
  "/fees": "/packages",
  "/partners": "/advisers",
  "/referrals": "/advisers",
  "/charities": "/giving",
  "/community": "/giving",
  "/diagnostic": "/scan",
  "/business-health-survey": "/scan",
  "/operations-health-score": "/scan",
  "/tools/scan": "/scan",
  "/tools/operating-scan": "/scan",
  "/scan/start": "/scan",
  "/assessment": "/scan",

  // Blog moved to /insights
  "/blog": "/insights",
  "/news": "/insights",
  "/articles": "/insights",
  "/resources": "/insights",

  // Renamed service pages
  "/services/eos": "/services/operating-system",
  "/services/business-operating-system": "/services/operating-system",
  "/services/people": "/services/team-structure",
  "/services/team": "/services/team-structure",
  "/services/strategy": "/services/strategy-execution",
  "/services/execution": "/services/strategy-execution",
  "/services/performance": "/services/performance-discipline",
  "/services/kpis": "/services/performance-discipline",
  "/services/projects": "/services/project-leadership",
  "/services/pmo": "/services/project-leadership",
  "/services/systems": "/services/systems-improvement",
  "/services/process-improvement": "/services/systems-improvement",
  "/services/equilibrium": "/services/team-structure",
  "/services/culture": "/services/team-structure",
  "/services/exit-ready": "/packages",
  "/exit-readiness": "/packages",
  "/exit-ready": "/packages",

  // Folded expertise pages
  "/specialisations/revenue-leakage-and-recovery": "/specialisations/pricing-and-revenue-management",

  // Charity pages
  "/giving/coco": "/giving/cocos",
  "/giving/coptic": "/giving/coptic-orphans",

  // Legal
  "/privacy-policy": "/privacy",
  "/terms-and-conditions": "/terms",
  "/terms-of-use": "/terms",
};

/** Blog posts that moved from /blog/<slug> keep their slug under /insights/<slug>. */
const PREFIX_REDIRECTS: Array<[string, string]> = [
  ["/blog/", "/insights/"],
  ["/news/", "/insights/"],
  ["/articles/", "/insights/"],
  ["/service/", "/services/"],
];

/**
 * Resolve a request path to its permanent destination, or null when the path
 * has no redirect (i.e. it is a genuine 404).
 */
export function resolveRedirect(pathname: string): string | null {
  const clean = pathname.split("?")[0].split("#")[0];
  const normalised =
    clean.length > 1 ? clean.replace(/\/+$/, "").toLowerCase() : clean.toLowerCase();

  let current: string | null = null;
  let next: string | undefined = REDIRECTS[normalised];

  if (!next) {
    for (const [from, to] of PREFIX_REDIRECTS) {
      if (normalised.startsWith(from)) {
        const rest = normalised.slice(from.length);
        if (rest) {
          next = `${to}${rest}`;
          break;
        }
      }
    }
  }

  // Follow exact-match hops so a prefix rewrite that lands on another
  // redirect key resolves in a single 301. Capped to avoid loops.
  let hops = 0;
  while (next && hops < 3) {
    current = next;
    next = REDIRECTS[current];
    if (next === current) break;
    hops += 1;
  }

  return current;
}
